import { useEffect, useMemo, useState, useRef } from "react";
import { HiExclamationCircle, HiChevronLeft, HiChevronRight } from "react-icons/hi2"
import { Tooltip } from "components/ui"
import { processSocialError } from "utils/socialErrorProcessor"

const PublishErrors = ({
    activeSocialNetwork,
    postGroup
}) => {
    
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [animationDirection, setAnimationDirection] = useState('');
    const [isTextTruncated, setIsTextTruncated] = useState(false);
    const textRef = useRef(null);

    const networkPublishErrors = useMemo(() => {
        // Regular production logic
        const errors = postGroup?.posts?.filter(p => p.social_network_type == activeSocialNetwork && p.status == "error" && !!p.status_message);
        
        if (!errors?.length) return [];
        
        // Process errors through the socialErrorProcessor utility
        const processedErrors = errors.map(p => {
            const processed = processSocialError(p.status_message, p.social_network_type);
            return {
                ...processed,
                originalMessage: p.status_message // Keep original for tooltip
            };
        });
        
        // Remove duplicates based on processed message
        const uniqueErrors = processedErrors?.filter((v, i, a) => 
            a.findIndex(v2 => (v2?.message === v?.message)) === i
        ) || [];
        
        return uniqueErrors;
    }, [postGroup, activeSocialNetwork]);

    useEffect(() => {
        // Reset index if current index is out of bounds
        if (currentIndex >= networkPublishErrors.length) {
            setCurrentIndex(0);
        }
    }, [networkPublishErrors, currentIndex]);

    // Check if text is truncated
    useEffect(() => {
        if (textRef.current) {
            const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
            setIsTextTruncated(isOverflowing);
        }
    }, [currentIndex, networkPublishErrors]);

    const handlePrevious = () => {
        if (isAnimating) return;
        
        setIsAnimating(true);
        setAnimationDirection('slide-left');
        
        setTimeout(() => {
            setCurrentIndex((prev) => 
                prev === 0 ? networkPublishErrors.length - 1 : prev - 1
            );
            setAnimationDirection('slide-in-right');
            
            setTimeout(() => {
                setIsAnimating(false);
                setAnimationDirection('');
            }, 200);
        }, 200);
    };

    const handleNext = () => {
        if (isAnimating) return;
        
        setIsAnimating(true);
        setAnimationDirection('slide-right');
        
        setTimeout(() => {
            setCurrentIndex((prev) => 
                prev === networkPublishErrors.length - 1 ? 0 : prev + 1
            );
            setAnimationDirection('slide-in-left');
            
            setTimeout(() => {
                setIsAnimating(false);
                setAnimationDirection('');
            }, 200);
        }, 200);
    };

    // Don't render if no errors
    if (!networkPublishErrors?.length) {
        return null;
    }

    const currentError = networkPublishErrors?.[currentIndex] || {};
    const totalErrors = networkPublishErrors.length;

    // Single error: show full message and action inline (no tooltip needed)
    if (totalErrors === 1) {
        return (
            <div className="flex items-start gap-1.5 py-1.5 px-2 bg-red-600/5 text-red-500 border border-red-200/20">
                <HiExclamationCircle className="text-base shrink-0 mt-[2px]" />
                <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="font-medium">{currentError?.message}</span>
                    <span className="text-xs opacity-75">{currentError?.action}</span>
                </div>
            </div>
        );
    }

    // Multiple errors: carousel with tooltip
    return (
        <div className="flex items-center gap-3 py-1 px-2 bg-red-600/5 text-red-500 border border-red-200/20">
            {/* Left Arrow */}
            <button
                onClick={handlePrevious}
                disabled={isAnimating}
                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-red-600/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500/30 disabled:opacity-50"
                aria-label="Previous error"
            >
                <HiChevronLeft className="text-lg" />
            </button>

            {/* Error Content with Animation */}
            <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
                <HiExclamationCircle className="text-base shrink-0 !mt-[1.5px]" />
                <div className="relative flex-1 min-w-0">
                    <Tooltip 
                        title={
                            <div className="max-w-sm">
                                <div className="font-semibold text-sm mb-1">{currentError?.message}</div>
                                <div className="text-xs mb-2">{currentError?.action}</div>
                                <div className="text-xs opacity-75 border-t border-white/10 pt-2">
                                    <div className="font-medium">Original error:</div>
                                    <div className="break-all">{currentError?.originalMessage?.length > 100 ? currentError?.originalMessage?.substring(0, 100) + '...' : currentError?.originalMessage}</div>
                                </div>
                            </div>
                        }
                        placement="top"
                    >
                        <div className="flex flex-col gap-0.5">
                            <span 
                                ref={textRef}
                                className={`block truncate transition-all duration-200 ease-in-out font-medium ${
                                    animationDirection === 'slide-left' ? 'transform -translate-x-full opacity-0' :
                                    animationDirection === 'slide-right' ? 'transform translate-x-full opacity-0' :
                                    animationDirection === 'slide-in-left' ? 'transform translate-x-0 opacity-100' :
                                    animationDirection === 'slide-in-right' ? 'transform translate-x-0 opacity-100' :
                                    'transform translate-x-0 opacity-100'
                                }`}
                            >
                                {currentError?.message}
                            </span>
                            <span 
                                className={`block truncate text-xs opacity-75 transition-all duration-200 ease-in-out ${
                                    animationDirection === 'slide-left' ? 'transform -translate-x-full opacity-0' :
                                    animationDirection === 'slide-right' ? 'transform translate-x-full opacity-0' :
                                    animationDirection === 'slide-in-left' ? 'transform translate-x-0 opacity-100' :
                                    animationDirection === 'slide-in-right' ? 'transform translate-x-0 opacity-100' :
                                    'transform translate-x-0 opacity-100'
                                }`}
                            >
                                {currentError?.action}
                            </span>
                        </div>
                    </Tooltip>
                </div>
            </div>

            {/* Counter and Right Arrow */}
            <div className="flex items-center gap-2 text-xs font-medium shrink-0">
                <span className="text-red-600">
                    {currentIndex + 1} of {totalErrors}
                </span>
            </div>

            <button
                onClick={handleNext}
                disabled={isAnimating}
                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-red-600/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500/30 disabled:opacity-50"
                aria-label="Next error"
            >
                <HiChevronRight className="text-lg" />
            </button>
        </div>
    );
}

/* 
 * To test the processSocialError functionality with hardcoded data:
 * 1. Pass enableTestMode={true} to the PublishErrors component
 * 2. The component will use TEST_ERROR_DATA instead of real post errors
 * 3. Check the browser console for test data logs
 * 4. Switch between different social networks to see different error types
 * 
 * Example usage:
 * <PublishErrors 
 *   activeSocialNetwork="twitter" 
 *   postGroup={postGroup} 
 *   enableTestMode={true} 
 * />
 */

export default PublishErrors;